import type { BBox } from '../detect/pii/types';

export type RedactRectMode = 'black' | 'blur';

export type RedactRect = BBox & {
  mode: RedactRectMode;
  /** Blur radius in pixels; only used when mode === 'blur'. */
  blurPx?: number;
};

export type ImageRedactInput = HTMLImageElement | ImageBitmap | HTMLCanvasElement;

export type ImageRedactOptions = {
  format?: 'image/png' | 'image/jpeg' | 'image/webp';
  quality?: number;
};

const dims = (src: ImageRedactInput): { width: number; height: number } => {
  if (src instanceof HTMLImageElement) {
    return { width: src.naturalWidth, height: src.naturalHeight };
  }
  return { width: src.width, height: src.height };
};

const drawSource = (
  ctx: CanvasRenderingContext2D,
  src: ImageRedactInput,
  width: number,
  height: number,
): void => {
  ctx.drawImage(src, 0, 0, width, height);
};

const drawBlackRect = (ctx: CanvasRenderingContext2D, r: BBox): void => {
  ctx.fillStyle = '#000';
  ctx.fillRect(r.x, r.y, r.w, r.h);
};

export const drawBlurRect = (
  ctx: CanvasRenderingContext2D,
  src: ImageRedactInput,
  r: BBox,
  blurPx: number,
): void => {
  // We deliberately avoid `ctx.filter = 'blur(...)'`: it is unimplemented
  // on iOS Safari pre-18 and buggy on several mobile WebKit builds.
  // Instead, downsample then upsample to fake a Gaussian blur, then
  // mask the result with a radial-gradient alpha so the redaction reads
  // as a soft ellipse instead of a hard square.
  const scale = Math.max(2, Math.round(blurPx / 2));
  const sw = Math.max(1, Math.round(r.w / scale));
  const sh = Math.max(1, Math.round(r.h / scale));
  const small = document.createElement('canvas');
  small.width = sw;
  small.height = sh;
  const sctx = small.getContext('2d');

  const fw = Math.max(1, Math.ceil(r.w));
  const fh = Math.max(1, Math.ceil(r.h));
  const feather = document.createElement('canvas');
  feather.width = fw;
  feather.height = fh;
  const fctx = feather.getContext('2d');

  if (!sctx || !fctx) {
    ctx.save();
    ctx.fillStyle = '#000';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.restore();
    return;
  }

  // Pass 1: blur into the per-rect feather canvas (still rectangular).
  sctx.imageSmoothingEnabled = true;
  sctx.imageSmoothingQuality = 'low';
  sctx.drawImage(src, r.x, r.y, r.w, r.h, 0, 0, sw, sh);
  fctx.imageSmoothingEnabled = true;
  fctx.imageSmoothingQuality = 'low';
  fctx.drawImage(small, 0, 0, sw, sh, 0, 0, fw, fh);

  // Pass 2: knock out the corners and feather the edges with a radial
  // gradient mask. Center solid out to ~65% of the radius (covers the
  // face proper after BlazeFace's bbox + our 30% pad) then ramps to
  // transparent. Using max(cx, cy) ensures elongated bboxes still get
  // their full long axis covered before feathering kicks in.
  const cx = fw / 2;
  const cy = fh / 2;
  const radius = Math.max(cx, cy);
  const mask = fctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
  mask.addColorStop(0.65, 'rgba(0, 0, 0, 1)');
  mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
  fctx.globalCompositeOperation = 'destination-in';
  fctx.fillStyle = mask;
  fctx.fillRect(0, 0, fw, fh);

  // Pass 3: composite the feathered ellipse onto the page canvas.
  ctx.drawImage(feather, r.x, r.y);
};

/**
 * Apply a list of redaction rectangles to a source image and return the
 * result as a Blob. EXIF and other metadata are dropped because the canvas
 * re-encodes from pixels — this is a feature, not an accident.
 */
export const redactImage = async (
  source: ImageRedactInput,
  rects: readonly RedactRect[],
  opts: ImageRedactOptions = {},
): Promise<Blob> => {
  const { width, height } = dims(source);
  if (width === 0 || height === 0) {
    throw new Error('redactImage: source has zero dimensions');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('redactImage: 2D context unavailable');

  drawSource(ctx, source, width, height);

  for (const rect of rects) {
    if (rect.w <= 0 || rect.h <= 0) continue;
    if (rect.mode === 'black') {
      drawBlackRect(ctx, rect);
    } else {
      drawBlurRect(ctx, source, rect, rect.blurPx ?? 20);
    }
  }

  const format = opts.format ?? 'image/png';
  const quality = opts.quality;

  return new Promise<Blob>((resolve, reject) => {
    const handle = (blob: Blob | null): void => {
      if (blob) resolve(blob);
      else reject(new Error('redactImage: canvas.toBlob returned null'));
    };
    if (typeof quality === 'number') {
      canvas.toBlob(handle, format, quality);
    } else {
      canvas.toBlob(handle, format);
    }
  });
};
